/**
 * The people we deal with at a property — list, add, edit — as a detail-panel
 * section.
 *
 * Stacie, 2026-08-11 test plan step 1d: "it also needs to list the contact name,
 * phone, email and have those editable so we can update real time if the contact
 * changes."
 *
 * The component itself is SupplierContacts, shared with airline and car vendors
 * since 2026-10-08; this file is the hotel adapter — the endpoint, the
 * 'left_property' status, the contract-feed fallback row, and the hotel copy. The
 * exported name and props are unchanged, so HotelDetail and AgreementDetail did
 * not move. See SupplierContacts for why the feed row is labelled and read-only.
 */
import {
  SupplierContacts,
  type ContactsAdapter,
  type SupplierContactRow,
} from './SupplierContacts';

/**
 * One contact as GET /api/btp/hotel-contacts returns it. Kept as its own name so
 * existing imports read as hotel contacts; the shape is SupplierContactRow.
 */
export type HotelContactRow = SupplierContactRow;

export interface HotelContactsProps {
  hotelCanonicalId: string;
  testId?: string;
}

const HOTEL_COPY: ContactsAdapter['copy'] = {
  parent: 'this property',
  retry: 'Reopen the row to retry.',
  empty:
    'No contact recorded for this property, and none on the contract feed either. ' +
    'Adding one here keeps it — the feed’s own contact column is overwritten by every sync.',
  allRetired:
    'Every contact recorded for this property has been removed as no longer there. ' +
    'They are kept below so past contact history still resolves to a name.',
  addButton: 'Add hotel point of contact',
  primaryLabel: 'Primary contact for this property',
  departedLabel:
    'They have left the property — keep the record, stop offering them',
  departedPrimaryHint: '— not available once they have left',
};

export function HotelContacts({
  hotelCanonicalId,
  testId = 'hotel-contacts',
}: HotelContactsProps) {
  return (
    <SupplierContacts
      adapter={{
        endpoint: '/api/btp/hotel-contacts',
        parentParam: 'hotel_canonical_id',
        parentId: hotelCanonicalId,
        departedStatus: 'left_property',
        hasFeed: true,
        copy: HOTEL_COPY,
      }}
      testId={testId}
    />
  );
}
